import React, { useEffect, useState } from 'react';
import { ArrowRight, Star, Clock, ChevronDown } from 'lucide-react';
import { restaurantInfo, heroGallery, hoursOfOperation, weeklySpecials } from '../config/siteConfig';
import { getNextBandDateLabel, getFutureBands } from '../utils/bandDateUtils';
import AmbientGlow from './AmbientGlow';

const Hero = () => {
  const todaysSpecial = weeklySpecials.days.find(
    (d) => d.day === new Date().toLocaleDateString('en-US', { weekday: 'long' })
  );
  const [loaded, setLoaded] = useState(false);
  const [liveMusicMessage, setLiveMusicMessage] = useState({ text: '', isTonight: false, isSoon: false });
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  // Randomize the gallery order once per page load (Fisher-Yates shuffle),
  // keeping the first image (the exterior) pinned as the initial slide
  const [galleryImages] = useState(() => {
    const [first, ...rest] = heroGallery.images;
    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    return [first, ...rest];
  });

  useEffect(() => {
    requestAnimationFrame(() => setLoaded(true));

    // Hide scroll indicator after scrolling
    const handleScroll = () => {
      setShowScrollIndicator(prev => {
        const next = window.scrollY < 200;
        return next === prev ? prev : next;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Auto-advance gallery
    const galleryInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
    }, heroGallery.autoAdvanceInterval);
    
    // Check for next upcoming band event using shared date utility
    const checkLiveMusic = () => {
      const futureBands = getFutureBands();
      if (!futureBands || futureBands.length === 0) {
        setLiveMusicMessage({ text: '', isTonight: false, isSoon: false });
        return;
      }

      const nextBand = futureBands[0];
      if (!nextBand || nextBand.band === 'TBD') {
        setLiveMusicMessage({ text: 'Live Music Coming Soon', isTonight: false, isSoon: false });
        return;
      }

      const dateLabel = getNextBandDateLabel();

      if (dateLabel === 'Tonight') {
        const startTime = nextBand.time.split(' - ')[0].trim();
        setLiveMusicMessage({ text: `Live Music Tonight @ ${startTime}`, isTonight: true, isSoon: false });
      } else if (dateLabel === 'Tomorrow Night') {
        setLiveMusicMessage({ text: `Live Music Tomorrow!`, isTonight: false, isSoon: true });
      } else if (dateLabel === 'Coming Soon') {
        setLiveMusicMessage({ text: 'Live Music Coming Soon', isTonight: false, isSoon: false });
      } else {
        // "This Saturday", "Next Saturday", "on 4/4", etc.
        setLiveMusicMessage({ text: `Live Music ${dateLabel}!`, isTonight: false, isSoon: true });
      }
    };

    checkLiveMusic();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(galleryInterval);
    };
  }, []);

  return (
    <section id="hero" className="relative bg-[#FDF6E9] texture-paper overflow-hidden pb-8">
      {/* Decorative elements */}
      <div className="hidden md:block absolute top-20 left-10 w-32 h-32 bg-[#C9A227]/5 rounded-full blur-3xl" />
      <div className="hidden md:block absolute bottom-40 right-20 w-40 h-40 bg-[#D4A574]/10 rounded-full blur-3xl" />

      {/* Warm drifting bokeh — the "bar lights" ambiance layer */}
      <AmbientGlow />

      
      {/* Satchmo's Logo - Large background silhouette */}
      <div 
        className={`absolute right-[-20%] sm:right-[-10%] top-[45%] sm:top-[35%] -translate-y-1/2 w-[140%] sm:w-[90%] h-[100%] sm:h-[120%] pointer-events-none transition-[opacity,transform] duration-[1500ms] ease-out ${
          loaded ? 'opacity-[0.10] sm:opacity-[0.08] translate-x-0' : 'opacity-0 translate-x-20'
        }`}
      >
        <img
          src="https://customer-assets.emergentagent.com/job_elegant-satchmos/artifacts/5uip0vr0_680-removebg-preview.png"
          alt=""
          className="w-full h-full object-contain"
          style={{ filter: 'brightness(0) sepia(1) saturate(0)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FDF6E9] via-transparent to-transparent" />
      </div>

      {/* Main content - extra top padding on smaller screens to clear logo */}
      <div className="relative z-10 max-w-6xl mx-auto px-5 pt-40 sm:pt-40 md:pt-44 lg:pt-40 xl:pt-36">
        <div className="max-w-2xl">
          {/* Badge Row - Est. 2013 and Live Music */}
          <div className={`flex flex-wrap items-center gap-3 mb-8 transition-[opacity,transform] duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="stamp text-[#C9A227] text-sm">
              <span className="flex items-center gap-2">
                <Star className="w-4 h-4 fill-current" />
                Est. 2013
              </span>
            </div>
            {liveMusicMessage.text && (
              <button 
                onClick={() => document.getElementById('live-music')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <span className={`w-2 h-2 rounded-full ${
                  liveMusicMessage.isTonight 
                    ? 'bg-green-500 animate-pulse' 
                    : 'bg-[#C9A227]'
                }`} />
                <span className={`font-display text-base tracking-widest uppercase ${
                  liveMusicMessage.isTonight || liveMusicMessage.isSoon 
                    ? 'text-[#C9A227]' 
                    : 'text-[#D4A574]'
                } hover:underline underline-offset-2`}>
                  {liveMusicMessage.text}
                </span>
              </button>
            )}
            {todaysSpecial && (
              <button
                onClick={() => document.getElementById('specials')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <span className="w-2 h-2 rounded-full bg-[#D4A574]" />
                <span className="font-display text-base tracking-widest uppercase text-[#D4A574] hover:underline underline-offset-2">
                  Today: {todaysSpecial.special}
                </span>
              </button>
            )}
          </div>

          {/* Main headline */}
          <h1 className={`transition-[opacity,transform] duration-700 delay-100 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="font-display text-6xl sm:text-7xl lg:text-8xl text-[#2C2416] leading-[0.9] tracking-wide block hero-headline">
              GOOD FOOD.
            </span>
            <span className="font-display text-6xl sm:text-7xl lg:text-8xl text-[#2C2416] leading-[0.9] tracking-wide block hero-headline">
              COLD DRINKS.
            </span>
            <span className="font-display text-6xl sm:text-7xl lg:text-8xl text-[#C9A227] leading-[0.9] tracking-wide block hero-headline">
              GREAT TIMES.
            </span>
          </h1>

          {/* Subtitle */}
          <p className={`mt-8 text-xl text-[#2C2416]/75 leading-relaxed max-w-lg transition-[opacity,transform] duration-700 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <span className="text-[#C9A227] font-semibold">Satchmo's</span> is your neighborhood bar & grill in <span className="text-[#C9A227] font-semibold">Chesterfield</span>. 
            Craft cocktails. Good food. The kind of place where everybody knows your name.
          </p>

          {/* Info bar */}
          <div className={`flex flex-wrap items-center gap-6 mt-6 text-sm text-[#2C2416]/70 transition-[opacity,transform] duration-700 delay-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Open Today: {hoursOfOperation[new Date().getDay()]?.closed ? 'Closed' : hoursOfOperation[new Date().getDay()]?.time}</span>
            </div>
            <div className="hidden sm:block w-1 h-1 bg-[#D4A574] rounded-full" />
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-[#C9A227] fill-[#C9A227]" />
              ))}
              <span className="ml-2">4.5 on Google</span>
            </div>
          </div>

          {/* CTAs */}
          <div className={`flex flex-wrap gap-4 mt-10 transition-[opacity,transform] duration-700 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <a href={restaurantInfo.orderOnlineUrl} target="_blank" rel="noopener noreferrer" className="btn-retro group flex items-center gap-3 animate-pulse-glow">
              ORDER ONLINE
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <button 
              onClick={() => document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 border-3 border-[#2C2416] text-[#2C2416] font-display text-xl tracking-wide hover:bg-[#2C2416] hover:text-[#FDF6E9] transition-all duration-300"
              style={{ borderWidth: '3px' }}
            >
              VIEW MENU
            </button>
          </div>
        </div>
      </div>

      {/* Scroll indicator - Fixed at bottom center with iOS safe area */}
      {showScrollIndicator && (
        <div 
          className={`fixed bottom-10 left-0 right-0 hidden sm:flex justify-center z-40 transition-all duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <button
            onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
            className="flex flex-col items-center gap-1 text-[#2C2416]/65 hover:text-[#C9A227] transition-colors group"
          >
            <span className="text-xs tracking-widest uppercase font-medium group-hover:tracking-[0.2em] transition-all">Scroll</span>
            <ChevronDown className="w-5 h-5 scroll-indicator" />
          </button>
        </div>
      )}

      {/* Restaurant Gallery */}
      <div className={`relative max-w-6xl mx-auto px-5 mt-16 pb-12 transition-[opacity,transform] duration-1000 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}>
        <div className="relative overflow-hidden rounded-lg shadow-2xl">
          {/* Gallery Container */}
          <div className="relative h-[350px] sm:h-[400px] lg:h-[450px]">
            {galleryImages.map((image, index) => (
              <img
                key={image.src}
                src={image.src}
                alt={image.alt}
                width={1200}
                height={450}
                decoding="async"
                loading={index === currentSlide ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : 'low'}
                className={`absolute inset-0 w-full h-full object-cover img-vintage transition-opacity duration-1000 ease-in-out ${
                  index === currentSlide
                    ? 'opacity-100 kenburns-slow'
                    : 'opacity-0'
                }`}
              />
            ))}
          </div>
          
          {/* Subtle vignette overlay */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at center, transparent 50%, rgba(44, 36, 22, 0.3) 100%)'
          }} />
          
          {/* Bottom gradient for dots visibility */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#2C2416]/50 to-transparent pointer-events-none" />
          
          {/* Dots indicator */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3">
            {galleryImages.map((image, index) => (
              <button
                key={image.src}
                onClick={() => setCurrentSlide(index)}
                className={`relative after:absolute after:-inset-3 after:content-[''] h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'bg-[#C9A227] w-10 shadow-lg' 
                    : 'bg-[#FDF6E9]/70 w-3 hover:bg-[#FDF6E9] hover:scale-110'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
